import React from "react";
import { formatCurrency, formatDate } from "../../../utils";
import moment from "moment";
import Logo from "../../../../src/assets/images/logo.png";
import paidStamp from "../../../assets/images/paid-stamp.png";

type LineItem = {
  description: string;
  quantity: number;
  unitPrice: number; // in cents
  amount: number; // in cents
  taxAmount?: number; // in cents
  planName?: string;
};

export interface PlanReceiptData {
  userDetails: {
    userId: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
    dateOfBirth?: string;
  };
  officeDetails: {
    officeAnnualPlanId: string;
    officeId: string;
    officeName: string;
    planName: string;
    planInterval: string;
    isActive: boolean;
    phone?: string;
    officeFax?: string;
    taxNumber?: string;
    officeAddress?: string;
    street?: string;
    city?: string;
    zip?: string;
    state?: string;
  };
  invoiceNumber: string;
  dateOfIssue: string;
  dateDue?: string;
  customerName: string;
  customerEmail: string;
  currency: string;
  subtotal: number; // in cents
  totalTax: number; // in cents
  total: number; // in cents
  amountDue: number; // in cents
  billingReason?: string;
  status?: string;
  lineItems: LineItem[];
  refundedAmount?: number;
}

const PlanReceiptPdf = ({ data }: { data: PlanReceiptData }) => {
  const { officeDetails, userDetails, invoiceNumber, subtotal, totalTax, total, lineItems, refundedAmount } = data;

  const formatCents = (val: number) => formatCurrency((val / 100).toString(), "blur");

  return (
    <>
      <>
        <div className="w-full h-a4 flex flex-col">
          <div className="px-8">
            <div className="flex justify-between">
              <span>
                <img src={Logo} alt="" className="w-48" crossOrigin="anonymous" />
              </span>
              <span>
                <img src={paidStamp} alt="" className="w-36 h-14" crossOrigin="anonymous" />
              </span>
            </div>
            <div className="flex justify-between">
              <p className="text-lg font-semibold mb-1 py-2">Invoice #{invoiceNumber}</p>
              <p className="text-lg font-semibold mb-1 py-2">Date: {moment().format("ll")}</p>
            </div>
            <div id="invoice-details">
              <div>
                <h2 className="text-xl mb-1 font-semibold">{officeDetails?.officeName}</h2>
                <p className="font-light ">{`${officeDetails?.street}, ${officeDetails?.city},`}</p>
                <p className="font-light">{` ${officeDetails?.state}, ${officeDetails?.zip}`}</p>
                <p className="font-light">{` Phone: ${officeDetails?.phone}`}</p>
                <p className="font-light">{` Fax: ${officeDetails?.officeFax}`}</p>
                {officeDetails?.taxNumber && <p className="font-light">{` HST # ${officeDetails?.taxNumber}`}</p>}
              </div>
            </div>
            <div id="invoice-patient-details" className="mt-3 mb-5">
              <p className="text font-semibold">Patient Details:</p>
              <p>
                {userDetails?.firstName} {userDetails?.lastName}
              </p>
              <p>Date of Birth: {formatDate(userDetails?.dateOfBirth ? userDetails?.dateOfBirth : "", "PP")}</p>
            </div>
          </div>
          <div id="invoice-fee" className="py-2  px-8" style={{ backgroundColor: "#F5F5F5" }}>
            <p className="text font-semibold mb-3">Fees</p>
          </div>

          <div className="px-8">
            {lineItems
              ?.slice()
              .reverse()
              ?.map((item, idx) => (
                <div className="flex" key={idx}>
                  <p className="w-full">{item.description}</p>
                  <p className="right-0 ml-auto">{formatCents(item.amount)}</p>
                </div>
              ))}
            <hr className="border border-black mt-4 mb-4" />
            <div className="flex">
              <p className="py-0.5 w-full ml-[50%]">Subtotal</p>
              <p className="right-0 mr-0">{formatCurrency((subtotal / 100).toString(), "blur")}</p>
            </div>
            <>
              <div>
                {totalTax > 0 ? (
                  <div className="flex">
                    <p className="py-0.5 w-full ml-[50%]">{"HST"}</p>
                    <p className="right-0 mr-0">{formatCurrency((totalTax / 100).toString(), "blur")}</p>
                  </div>
                ) : null}
              </div>
              <hr className="border border-black mt-4 mb-4" />
            </>
            <div className="flex">
              <p className="py-0.5 w-full ml-[50%] font-semibold">Invoice total</p>
              <p className="right-0 mr-0 font-semibold">{formatCurrency((total / 100).toString(), "blur")}</p>
            </div>
            {refundedAmount && (
              <div className="flex">
                <p className="py-0.5 w-full ml-[50%] font-semibold">Refunded Amount</p>
                <p className="right-0 mr-0 font-semibold">{formatCurrency((refundedAmount / 100).toString(), "blur")}</p>
              </div>
            )}
          </div>
        </div>
        <div>
          {" "}
          <p className="text-xs text-center text-docsigna-purple">481 London Road, Unit 109, Sarnia, ON N7T 4X3 HST # 703533687 RT 0001</p>
          <p className=" text-xs text-center text-docsigna-purple mb-5">This service has been provided by Docnote Inc.</p>
        </div>
      </>
    </>
  );
};

export default PlanReceiptPdf;
